import { useEffect, useRef, ReactNode, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from '@emotion/styled'
import { rgba } from 'polished'

import { useTranslation } from 'next-i18next'

import { colors, zIndex, border } from '@/styles/theme'
import { mediaUp, mediaDown } from '@/styles/media'
import CommonButton from '@/components/form/Button/CommonButton'

interface Props {
  show: boolean
  onClose(): void
  children: JSX.Element | ReactNode
}

function ModalMap(props: Props) {
  const { show = false, onClose, children } = props
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const { t } = useTranslation('parkings')

  useEffect(() => {
    containerRef.current = document.querySelector('#modal-root')
  }, [])

  useEffect(() => {
    if (show) {
      setIsOpen(true)
    }
  }, [show])

  const handleCloseClick = () => {
    setIsOpen(false)
    setTimeout(() => onClose(), 500)
  }

  const modalContent = show && (
    <Wrapper isOpen={isOpen}>
      <Container isOpen={isOpen}>
        <Header>
          <Title>{t('searchNearParking.filters.mapModal.title')}</Title>
          <CommonButton
            variant="close"
            onClick={handleCloseClick}
            customStyle={{ fontSize: '24px' }}
          >
            {'×'}
          </CommonButton>
        </Header>
        {children}
      </Container>
    </Wrapper>
  )

  return (
    <>
      {containerRef.current
        ? createPortal(modalContent, containerRef.current)
        : null}
    </>
  )
}

const Wrapper = styled.div<{ isOpen: boolean }>`
  position: fixed;
  width: 100%;
  height: 100%;
  top: -100vh;
  left: 0;
  background: ${rgba(colors.twilightBlue, 0.06)};
  z-index: ${zIndex.modalWrapper};
  display: flex;
  justify-content: center;
  align-items: center;
  backdrop-filter: blur(2px);
  opacity: 0;
  transition: opacity 0.5s;
  ${({ isOpen }) => isOpen && `opacity: 1;`}

  @supports not (backdrop-filter: blur(2px)) {
    background: ${rgba(colors.twilightBlue, 0.45)};
  }

  ${mediaDown('md')} {
    justify-content: flex-start;
    align-items: flex-start;
  }
  ${mediaUp('lg')} {
    top: 0;
  }
`

const Container = styled.div<{ isOpen: boolean }>`
  position: relative;
  justify-content: center;
  border-radius: 28px;
  box-shadow: ${border.shadow};
  background-color: ${colors.white};
  z-index: ${zIndex.modal};
  padding: 24px;
  max-height: calc(100vh - 40px);
  align-items: safe center;
  overflow: auto;

  ${mediaDown('lg')} {
    transform: translate(0%, 100%);
    transition: transform 0.5s;
    width: 100%;
    border-bottom-right-radius: 0;
    border-bottom-left-radius: 0;
  }

  ${mediaDown('md')} {
    width: 100vw;
    height: 100vh;
    margin: 0;
    border-radius: 0;
    padding: 24px;
    max-height: 100vh;
  }

  ${mediaUp('lg')} {
    width: 1020px;
    overflow: visible;
  }

  ${({ isOpen }) =>
    mediaDown('lg') && isOpen && `transform: translate(0%, 0%);`}
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`

const Title = styled.div`
  font-size: 24px;
  font-weight: bold;
`

export default ModalMap
